import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Dashboard from './pages/Dashboard';
import ImageList from './pages/ImageList';
import ImageDetail from './pages/ImageDetail';
import Blueprints from './pages/Blueprints';
import BuildTrigger from './pages/BuildTrigger';
import BuildStatus from './pages/BuildStatus';
import BuildHistory from './pages/BuildHistory';
import Schedules from './pages/Schedules';
import Usage from './pages/Usage';
import Support from './pages/Support';
import HealthCheck from './pages/HealthCheck';
import ComplianceReport from './pages/ComplianceReport';
import ImageComparison from './pages/ImageComparison';
import BuildMatrix from './pages/BuildMatrix';
import Lifecycle from './pages/Lifecycle';
import Notifications from './pages/Notifications';
import HealthScorecard from './pages/HealthScorecard';
import SBOMViewer from './pages/SBOMViewer';
import ImageLineage from './pages/ImageLineage';
import Channels from './pages/Channels';
import Policies from './pages/Policies';
import TestResults from './pages/TestResults';
import Fleet from './pages/Fleet';
import FleetInstances from './pages/FleetInstances';
import { Toaster } from 'sonner';
import { setCurrentToken } from './utils/auth';
import { ThemeToggle } from './components/ThemeToggle';
import { CommandPalette } from './components/CommandPalette';
import './App.css';

function NavLink({ to, children }) {
  const location = useLocation();
  const active = location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <Link to={to} className={`nav-link ${active ? 'active' : ''}`}>
      {children}
    </Link>
  );
}

function UserMenu({ user, auth }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const initials = (user?.email || user?.name || 'U').charAt(0).toUpperCase();

  return (
    <div className="user-menu" ref={ref}>
      <button className="user-menu-trigger user-avatar" onClick={() => setOpen(!open)} title={user?.email || user?.name}>
        {initials}
      </button>
      {open && (
        <div className="user-menu-dropdown">
          <div className="user-menu-item" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '12px', color: '#b2bec3' }}>{user?.email || user?.name || 'User'}</span>
          </div>
          <div className="user-menu-item">
            <span>Theme</span>
            <span style={{ marginLeft: 'auto' }}><ThemeToggle /></span>
          </div>
          <button onClick={() => { auth.removeUser(); setOpen(false); }}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function App() {
  const auth = useAuth();

  // Keep in-memory token in sync with OIDC state
  useEffect(() => {
    if (auth.user?.access_token) {
      setCurrentToken(auth.user.access_token);
    } else if (auth.user?.id_token) {
      setCurrentToken(auth.user.id_token);
    }
  }, [auth.user]);

  // Loading state while OIDC is initializing
  if (auth.isLoading) {
    return (
      <div className="loading">
        <p>Loading...</p>
      </div>
    );
  }

  // Error state
  if (auth.error) {
    return (
      <div className="app">
        <header className="app-header">
          <h1>Image Factory</h1>
        </header>
        <main className="app-main">
          <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h2 style={{ marginBottom: '16px' }}>Authentication Error</h2>
            <p style={{ color: '#636e72', marginBottom: '24px' }}>
              {auth.error.message}
            </p>
            <button onClick={() => auth.signinRedirect()} className="btn btn-primary">
              Try again
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Not authenticated — show login
  if (!auth.isAuthenticated) {
    return (
      <div className="app">
        <header className="app-header">
          <h1>Image Factory</h1>
        </header>
        <main className="app-main">
          <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h2 style={{ marginBottom: '16px' }}>Sign in required</h2>
            <p style={{ color: '#636e72', marginBottom: '24px' }}>
              Please sign in with your organization account to continue.
            </p>
            <button onClick={() => auth.signinRedirect()} className="btn btn-primary">
              Sign in with SSO
            </button>
          </div>
        </main>
      </div>
    );
  }

  const user = auth.user?.profile;

  return (
    <div className="app">
      <header className="app-header">
        <Link to="/" className="app-logo-link">
          <h1>Image Factory</h1>
        </Link>
        <nav>
          <NavLink to="/dashboard">Dashboard</NavLink>
          <NavLink to="/images">Images</NavLink>
          <NavLink to="/blueprints">Blueprints</NavLink>
          <NavLink to="/build">Build</NavLink>
          <NavLink to="/build-matrix">Build Matrix</NavLink>
          <NavLink to="/history">History</NavLink>
          <NavLink to="/compare">Compare</NavLink>
          <NavLink to="/lifecycle">Lifecycle</NavLink>
          <NavLink to="/schedules">Schedules</NavLink>
          <NavLink to="/notifications">Notifications</NavLink>
          <NavLink to="/usage">Usage</NavLink>
          <NavLink to="/support">Support</NavLink>
        </nav>
        <UserMenu user={user} auth={auth} />
      </header>
      <CommandPalette />
      <Toaster position="top-right" richColors closeButton />
      <main className="app-main">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/callback" element={<Navigate to="/" replace />} />
          <Route path="/health" element={<HealthCheck />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/images" element={<ImageList />} />
          <Route path="/images/:id" element={<ImageDetail />} />
          <Route path="/blueprints" element={<Blueprints />} />
          <Route path="/build" element={<BuildTrigger />} />
          <Route path="/build/:workflowName" element={<BuildStatus />} />
          <Route path="/history" element={<BuildHistory />} />
          <Route path="/compliance/:imageId" element={<ComplianceReport />} />
          <Route path="/compare" element={<ImageComparison />} />
          <Route path="/build-matrix" element={<BuildMatrix />} />
          <Route path="/lifecycle" element={<Lifecycle />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/schedules" element={<Schedules />} />
          <Route path="/scorecard/:imageId" element={<HealthScorecard />} />
          <Route path="/sbom/:imageId" element={<SBOMViewer />} />
          <Route path="/lineage/:imageId" element={<ImageLineage />} />
          <Route path="/channels" element={<Channels />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/tests/:imageId" element={<TestResults />} />
          <Route path="/fleet" element={<Fleet />} />
          <Route path="/fleet/instances" element={<FleetInstances />} />
          <Route path="/usage" element={<Usage />} />
          <Route path="/support" element={<Support />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;
